import * as React from "react";

import {
  format,
  getMonth,
  getYear,
  isValid,
  parse,
  parseISO,
  setMonth,
  setYear,
} from "date-fns";

import { cn } from "../../../lib/utils";
import { Button } from "../../ui/Button";
import { Calendar } from "../../ui/Calendar";
import { LuCalendar } from "react-icons/lu";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/PopOver";
import { ScrollArea, ScrollBar } from "../../ui/ScrollArea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/Select";

type IDateTimePicker = {
  startYear?: number;
  endYear?: number;
  onChange: (date_string: string) => void;
  label?: string;
  error?: string; // Added error type
  value?: string;
};

const parseDateValue = (value: string): Date | null => {
  const referenceDate = new Date();
  const parsedDate = [
    parse(value, "yyyy-MM-dd HH:mm:ss", referenceDate),
    parse(value, "yyyy-MM-dd hh:mm aa", referenceDate),
    parse(value, "yyyy-MM-dd hh:mm:ss", referenceDate),
  ].find(isValid);

  if (parsedDate) return parsedDate;

  const isoDate = parseISO(value);
  if (isValid(isoDate)) return isoDate;

  const date = new Date(value);
  return isValid(date) ? date : null;
};

export default function DateTimePicker({
  startYear = getYear(new Date()) - 100,
  endYear = getYear(new Date()) + 100,
  onChange,
  label,
  error,
  value,
}: IDateTimePicker) {
  const [date, setDate] = React.useState<Date | null>(null);
  const [isOpen, setIsOpen] = React.useState(false);

  React.useEffect(() => {
    if (value === undefined) return;
    setDate(value ? parseDateValue(value) : null);
  }, [value]);

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const years = Array.from(
    { length: endYear - startYear + 1 },
    (_, i) => startYear + i,
  );

  const handleDateSelect = (selectedDate: Date | undefined) => {
    if (selectedDate) {
      setDate(selectedDate);
      onChange?.(format(selectedDate, "yyyy-MM-dd hh:mm aa"));
    }
  };

  const handleMonthChange = (month: string) => {
    // if date is null, start from today's date
    const baseDate = date ?? new Date();
    const newDate = setMonth(baseDate, months.indexOf(month));
    setDate(newDate);
    onChange?.(format(newDate, "yyyy-MM-dd hh:mm aa"));
  };

  const handleYearChange = (year: string) => {
    const baseDate = date ?? new Date();
    const newDate = setYear(baseDate, parseInt(year));
    setDate(newDate);
    onChange?.(format(newDate, "yyyy-MM-dd hh:mm aa"));
  };

  const hours = Array.from({ length: 12 }, (_, i) => i + 1);

  const handleTimeChange = (
    type: "hour" | "minute" | "ampm",
    value: string,
  ) => {
    if (date) {
      const newDate = new Date(date);
      if (type === "hour") {
        newDate.setHours(
          (parseInt(value) % 12) + (newDate.getHours() >= 12 ? 12 : 0),
        );
      } else if (type === "minute") {
        newDate.setMinutes(parseInt(value));
      } else if (type === "ampm") {
        const currentHours = newDate.getHours();
        newDate.setHours(
          value === "PM" ? currentHours + 12 : currentHours - 12,
        );
      }
      setDate(newDate);
      onChange?.(format(newDate, "yyyy-MM-dd hh:mm aa"));
    }
  };

  return (
    <div>
      {label && (
        <label htmlFor="date" className="w-full block mb-2 font-bold">
          {label}
        </label>
      )}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="default"
            className={cn(
              "w-full justify-start text-left font-normal",
              !date && "text-[var(--muted-foreground)]",
            )}
          >
            <LuCalendar className="mr-2" />
            {date ? (
              format(date, "yyyy-MM-dd hh:mm aa")
            ) : (
              <span>YYYY-MM-DD hh:mm aa</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0">
          <div className="sm:flex h-[380px]">
            <div className="py-2">
              <div className="flex items-center justify-center gap-2 ">
                <Select
                  onValueChange={handleMonthChange}
                  value={date ? months[getMonth(date)] : ""}
                >
                  <SelectTrigger className="w-[110px] shadow-none">
                    <SelectValue placeholder="Month" />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map((month) => (
                      <SelectItem key={month} value={month}>
                        {month}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  onValueChange={handleYearChange}
                  value={date ? getYear(date).toString() : ""}
                >
                  <SelectTrigger className="w-[110px] shadow-none">
                    <SelectValue placeholder="Year" />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map((year) => (
                      <SelectItem key={year} value={year.toString()}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Calendar
                mode="single"
                selected={date ?? undefined}
                onSelect={handleDateSelect}
                initialFocus
                month={date ?? new Date()}
                onMonthChange={setDate}
              />
            </div>
            <div className="flex flex-col sm:flex-row sm:min-h-[345px] divide-y sm:divide-y-0 sm:divide-x">
              <ScrollArea className="h-full w-64 sm:w-auto">
                <div className="flex sm:flex-col p-2">
                  {hours.reverse().map((hour) => (
                    <Button
                      key={hour}
                      size="icon"
                      variant={
                        date && date.getHours() % 12 === hour % 12
                          ? "default"
                          : "ghost"
                      }
                      className="sm:w-full shrink-0 aspect-square"
                      onClick={() => handleTimeChange("hour", hour.toString())}
                    >
                      {hour}
                    </Button>
                  ))}
                </div>
                <ScrollBar orientation="horizontal" className="sm:hidden " />
              </ScrollArea>
              <ScrollArea className="w-64 sm:w-auto">
                <div className="flex sm:flex-col p-2">
                  {Array.from({ length: 60 }, (_, i) => i).map((minute) => (
                    <Button
                      key={minute}
                      size="icon"
                      variant={
                        date && date.getMinutes() === minute
                          ? "default"
                          : "ghost"
                      }
                      className="sm:w-full shrink-0 aspect-square"
                      onClick={() =>
                        handleTimeChange("minute", minute.toString())
                      }
                    >
                      {minute}
                    </Button>
                  ))}
                </div>
                <ScrollBar orientation="horizontal" className="sm:hidden" />
              </ScrollArea>
              <ScrollArea className="">
                <div className="flex sm:flex-col p-2">
                  {["AM", "PM"].map((ampm) => (
                    <Button
                      key={ampm}
                      size="icon"
                      variant={
                        date &&
                        ((ampm === "AM" && date.getHours() < 12) ||
                          (ampm === "PM" && date.getHours() >= 12))
                          ? "default"
                          : "ghost"
                      }
                      className="sm:w-full shrink-0 aspect-square"
                      onClick={() => handleTimeChange("ampm", ampm)}
                    >
                      {ampm}
                    </Button>
                  ))}
                </div>
              </ScrollArea>
            </div>
          </div>
          <div
            className="flex justify-between items-center 
          border-t border-t-gray-200 p-2"
          >
            <Button
              size="sm"
              variant="ghost"
              className="p-0 text-primary-500 hover:text-primary-700 cursor-pointer hover:bg-transparent"
              onClick={() => {
                const now = new Date();
                setDate(now);
                onChange?.(format(now, "yyyy-MM-dd hh:mm aa"));
              }}
            >
              Now
            </Button>
            <Button
              size="sm"
              className="px-2 py-1"
              onClick={() => {
                if (date) {
                  onChange?.(format(date, "yyyy-MM-dd hh:mm aa"));
                }
                setIsOpen(false);
              }}
            >
              OK
            </Button>
          </div>
        </PopoverContent>
      </Popover>
      {error && <span className="text-xs font-normal">{error}</span>}
    </div>
  );
}
